// The one place that decides which unrequested popup is on screen (rules in
// popupRules.ts). A popup asks with useDirectedPopup(id, wants) and renders only
// while it returns true; the map's <PopupDirectorGate> reports when another map
// window is open.
import { useEffect, useLayoutEffect } from 'react';
import { create } from 'zustand';
import {
  GAP_MS,
  SETTLE_MS,
  ask,
  endPause,
  initialDirectorState,
  setBusy,
  startPause,
  visiblePopup,
  withdraw,
  type DirectorState,
  type PopupId,
} from './popupRules';

export const usePopupDirectorStore = create<DirectorState>(() => initialDirectorState());

let pauseTimer: ReturnType<typeof setTimeout> | undefined;

function pauseFor(ms: number): void {
  clearTimeout(pauseTimer);
  usePopupDirectorStore.setState(startPause(usePopupDirectorStore.getState()));
  pauseTimer = setTimeout(() => {
    usePopupDirectorStore.setState(endPause(usePopupDirectorStore.getState()));
  }, ms);
}

export function askPopup(id: PopupId): void {
  usePopupDirectorStore.setState(ask(usePopupDirectorStore.getState(), id));
}

export function withdrawPopup(id: PopupId): void {
  const { state, pause } = withdraw(usePopupDirectorStore.getState(), id);
  usePopupDirectorStore.setState(state);
  if (pause) pauseFor(GAP_MS);
}

export function setPopupsBusy(busy: boolean): void {
  const { state, pause } = setBusy(usePopupDirectorStore.getState(), busy);
  usePopupDirectorStore.setState(state);
  if (pause) pauseFor(GAP_MS);
}

/** The map just opened: give every popup a moment to ask before one is picked. */
export function openPopupStage(): void {
  pauseFor(SETTLE_MS);
}

/**
 * Ask to show popup `id` while `wants` is true. Returns whether to render it now.
 * The popup must stop wanting (its close handler) for the next one to come.
 */
export function useDirectedPopup(id: PopupId, wants: boolean): boolean {
  useEffect(() => {
    if (!wants) return;
    askPopup(id);
    return () => withdrawPopup(id);
  }, [id, wants]);
  const visible = usePopupDirectorStore((s) => visiblePopup(s) === id);
  return wants && visible;
}

/**
 * Mounted once on the map. `busy`: another map window is open (a house card, the
 * tutorial, a purchase…) — directed popups wait for it. Synced before paint, so a
 * popup never flashes over the window that just opened.
 */
export function PopupDirectorGate({ busy }: { busy: boolean }): null {
  useLayoutEffect(() => {
    setPopupsBusy(busy);
  }, [busy]);
  // Stryker disable ArrayDeclaration: any constant dependency list means "on mount only", same as [].
  useEffect(() => {
    openPopupStage();
  }, []);
  // Stryker restore ArrayDeclaration
  return null;
}
